import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSlash } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import {
  extractSeasonWindows,
  isSeasonWindowOpen,
} from '../utilities/transferWindows';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/transferWindows.css';

export default function ManageSeasonTransferWindows() {
  const { leagueId } = useParams();
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('leagues');

  const [leagueName, setLeagueName] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    loadSeasons();
    return undefined;
  }, [leagueId, champId, champsLoading]);

  const applyPayload = (payload) => {
    const { league, seasons: nextSeasons } = extractSeasonWindows(payload);
    setLeagueName(league?.name || '');
    setSeasons(nextSeasons);
    return league;
  };

  const loadSeasons = async () => {
    if (!champId || !leagueId) {
      setLoading(false);
      setMessage({ text: t('twSeasonLoadError'), type: 'error' });
      return;
    }
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminSeasonTransferWindows, {
        champ_id: champId,
        league_id: Number(leagueId),
      });
      const league = applyPayload(response.data);
      if (!league) {
        setMessage({ text: t('twNotFound'), type: 'error' });
      }
    } catch (error) {
      setSeasons([]);
      setMessage({ text: getApiErrorMessage(error, t('twSeasonLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (season) => {
    if (!canWrite || savingId != null) return;
    setSavingId(season.id);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.changeAdminSeasonTransferWindow, {
        champ_id: champId,
        league_id: Number(leagueId),
        season_id: Number(season.id),
        application_active: isSeasonWindowOpen(season) ? 0 : 1,
      });
      applyPayload(response.data);
      setMessage({ text: t('twSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('twSeasonSaveError')), type: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  const columns = [
    {
      key: 'status',
      label: t('twStatus'),
      className: 'st-cell-status',
      render: (item) => (
        <span className={`st-badge ${isSeasonWindowOpen(item) ? 'enabled' : 'disabled'}`}>
          {isSeasonWindowOpen(item) ? t('twSeasonOpen') : t('twSeasonClosed')}
        </span>
      ),
    },
    {
      key: 'name',
      label: t('twSeasonName'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="tw-season-name">
          <span>{item.name || '—'}</span>
          {Number(item.is_active) === 1 ? <span className="st-badge enabled">{t('twSeasonActive')}</span> : null}
        </span>
      ),
    },
  ];

  if (canWrite) {
    columns.push({
      key: 'action',
      label: '',
      className: 'tw-cell-action',
      render: (item) => (
        <button
          type="button"
          className={`st-btn ${isSeasonWindowOpen(item) ? 'st-btn-secondary' : 'st-btn-primary'}`}
          disabled={savingId != null}
          onClick={(event) => {
            event.stopPropagation();
            handleToggle(item);
          }}
        >
          {savingId === item.id
            ? t('trLoading')
            : (isSeasonWindowOpen(item) ? t('twCloseWindow') : t('twOpenWindow'))}
        </button>
      ),
    });
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navTransferWindows'), to: '/transfer_windows' },
                { label: leagueName || t('twSeasonsTitle') },
              ]}
            />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-table-wrap">
            {loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                <p>{t('twLoading')}</p>
              </div>
            ) : (
              <ReorderableTable
                tableClassName="st-table-tw-seasons"
                columns={columns}
                items={seasons}
                getRowKey={(item) => item.id}
                reorderMode={false}
                onItemsChange={() => {}}
                empty={(
                  <div className="st-empty">
                    <FiSlash size={48} />
                    <h3>{t('twNoSeasons')}</h3>
                    <p>{t('twNoSeasonsHint')}</p>
                  </div>
                )}
              />
            )}
          </div>
        </div>

        <div className="st-actions">
          <Link to="/transfer_windows" className="st-btn st-btn-secondary">
            {t('stBack')}
          </Link>
        </div>
      </div>
    </div>
  );
}
